import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { InstallPrompt } from "@/components/InstallPrompt";
import { DisplayNameEditor } from "@/components/DisplayNameEditor";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtTime } from "@/lib/api";

const MEDAL = { 1: "text-amber-400", 2: "text-slate-300", 3: "text-orange-400" };
const ordinal = (n) => { const s = ["th", "st", "nd", "rd"], v = n % 100; return n + (s[(v - 20) % 10] || s[v] || s[0]); };

export default function Leaderboard() {
  const { user } = useAuth();
  const [lb, setLb] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (user === null) return;
    api.get("/leaderboard").then(({ data }) => setLb(data)).catch((e) => setError(errMsg(e)));
  }, [user]);

  if (error) return <Shell><p className="text-sm text-rose-300" data-testid="leaderboard-error">{error}</p></Shell>;
  if (!lb) return <Shell><p className="eyebrow mt-10">Loading leaderboard…</p></Shell>;

  const prizeFor = (rank) => lb.prizes?.find((p) => p.place === rank)?.reward;
  const ended = lb.status === "ended";
  return (
    <Shell width="wide">
      <div className="fade-up">
        <p className="eyebrow text-amber-400" data-testid="leaderboard-eyebrow">Singapore Logic Beta</p>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight md:text-4xl" data-testid="leaderboard-title">{ended ? "Final standings" : "Leaderboard"}</h1>
        <p className="mt-1 text-sm text-slate-400" data-testid="leaderboard-player-count">{lb.status === "live" ? `Day ${lb.day} of ${lb.total_days}` : ended ? "Competition over" : "Opens soon"} · {lb.players} {lb.players === 1 ? "player" : "players"} · <span className="text-slate-200">ties broken by total solving time</span></p>
        {lb.prizes?.length > 0 && (
          <div className="panel mt-4 p-4" data-testid="leaderboard-prizes">
            <p className="eyebrow mb-2 text-amber-400">{ended ? "Winners" : `Rewards · end of Day ${lb.total_days}`}</p>
            <ul className="grid gap-1.5 sm:grid-cols-2">
              {lb.prizes.map((p) => {
                const winner = ended ? lb.rows.find((r) => r.rank === p.place) : null;
                return <li key={p.place} className="flex items-center gap-2 text-sm" data-testid={`leaderboard-prize-${p.place}`}><span className="font-mono text-xs font-bold text-amber-300">{ordinal(p.place)}</span><span className="text-slate-200">{p.reward}</span>{winner && <span className="ml-auto truncate font-semibold text-emerald-300">{winner.display_name}</span>}</li>;
              })}
            </ul>
          </div>
        )}

        {lb.me && (
          <div className="panel mt-6 flex items-center justify-between border-blue-500/50 bg-blue-500/10 p-4" data-testid="leaderboard-me-card">
            <div className="min-w-0">
              <p className="eyebrow text-blue-300">Your position{lb.me.rank && lb.me.rank > lb.rows.length ? ` · outside top ${lb.rows.length}` : ""}</p>
              <p className="mt-1 font-display text-lg font-semibold" data-testid="leaderboard-me-name">{lb.me.display_name}</p>
              {!lb.me.rank && <p className="text-xs text-slate-400">Not ranked yet — play today's challenge</p>}
              <DisplayNameEditor onSaved={(u) => setLb({ ...lb, me: { ...lb.me, display_name: u.display_name }, rows: lb.rows.map((r) => (r.user_id === u.id ? { ...r, display_name: u.display_name } : r)) })} />
            </div>
            <div className="text-right">
              <p className="font-mono text-2xl font-bold text-white" data-testid="leaderboard-me-rank">{lb.me.rank ? `#${lb.me.rank}` : "—"}</p>
              <p className="font-mono text-xs text-slate-400" data-testid="leaderboard-me-points">{lb.me.total_points} pts · {fmtTime(lb.me.total_response_ms)}</p>
            </div>
          </div>
        )}

        <ol className="panel mt-4 divide-y divide-slate-800" data-testid="leaderboard-list">
          <li className="grid grid-cols-[2rem_1fr_auto] gap-4 px-5 py-2 eyebrow md:grid-cols-[3rem_1fr_auto]"><span>Rank</span><span>Display name</span><span className="text-right">Points · Time</span></li>
          {lb.rows.length === 0 && <li className="p-6 text-sm text-slate-400" data-testid="leaderboard-empty">No scores yet. Be the first to play today's challenge.</li>}
          {lb.rows.map((row) => {
            const me = lb.me?.user_id === row.user_id;
            return (
              <li key={row.user_id} className={`flex items-center gap-4 px-5 py-3.5 ${me ? "bg-blue-500/10" : ""}`} data-testid={`leaderboard-row-${row.rank}`}>
                <span className={`w-8 font-mono text-base font-bold md:w-12 ${MEDAL[row.rank] || "text-slate-500"}`}>{row.rank}</span>
                <span className="flex-1 truncate text-base font-medium">{row.display_name}{me && <span className="ml-2 text-xs text-blue-300">you</span>}{prizeFor(row.rank) && <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-300" data-testid={`leaderboard-row-prize-${row.rank}`}>{prizeFor(row.rank)}</span>}</span>
                <span className="text-right">
                  <span className="block font-mono text-base font-semibold text-white">{row.total_points}</span>
                  <span className="block font-mono text-[11px] text-slate-500" data-testid={`leaderboard-time-${row.rank}`}>{fmtTime(row.total_response_ms)}</span>
                </span>
              </li>
            );
          })}
        </ol>
        <div className="max-w-md"><InstallPrompt /></div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          {user ? <Link to="/play" className="btn-primary sm:min-w-[220px]" data-testid="leaderboard-play-button">Today's challenge</Link>
                : <Link to="/register" className="btn-primary sm:min-w-[220px]" data-testid="leaderboard-register-button">Join the competition</Link>}
        </div>
      </div>
    </Shell>
  );
}
